class CollaborativeCursorManager {
    constructor(websocket, userId) {
        this.ws = websocket;
        this.userId = userId;
        this.otherCursors = new Map();  // userId -> DOM element
        this.throttleTimer = null;
        this.THROTTLE_MS = 50;  // Send updates max every 50ms
        this.pointersVisible = true;  // Track visibility state

        // Get the simulation viewport element for coordinate calculation
        this.viewport = document.getElementById('viewport') || document.querySelector('.viewport-container');

        this.cursorContainer = document.getElementById('collaborative-cursors');
        if (!this.cursorContainer) {
            this.cursorContainer = document.createElement('div');
            this.cursorContainer.id = 'collaborative-cursors';
            document.body.appendChild(this.cursorContainer);
        }

        this.createToggleButton();
        this.init();
    }

    createToggleButton() {
        // Create toggle pointers button (floating, similar to chat button)
        this.toggleButton = document.createElement('button');
        this.toggleButton.id = 'toggle-pointers-button';
        this.toggleButton.className = 'toggle-pointers-button active';
        this.toggleButton.title = 'Show/hide other users\' pointers';
        this.toggleButton.innerHTML = `
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M13.64 21.97C13.14 22.21 12.54 22 12.31 21.5L10.13 16.76L7.62 18.78C7.45 18.92 7.24 19 7.02 19C6.55 19 6.16 18.61 6.16 18.14V5.51C6.16 5.04 6.55 4.65 7.02 4.65C7.23 4.65 7.43 4.73 7.59 4.86L18.35 14.14C18.63 14.39 18.72 14.79 18.57 15.14C18.42 15.49 18.07 15.72 17.69 15.72H14.09L16.27 20.46C16.5 20.97 16.29 21.56 15.78 21.79L13.64 21.97Z"/>
            </svg>
        `;
        document.body.appendChild(this.toggleButton);

        // Add click handler
        this.toggleButton.addEventListener('click', () => this.togglePointerVisibility());
    }

    togglePointerVisibility() {
        this.pointersVisible = !this.pointersVisible;

        // Update button appearance
        if (this.pointersVisible) {
            this.toggleButton.classList.add('active');
            this.toggleButton.title = 'Hide other users\' pointers';
        } else {
            this.toggleButton.classList.remove('active');
            this.toggleButton.title = 'Show other users\' pointers';
        }

        // Show/hide all cursor elements
        this.cursorContainer.style.display = this.pointersVisible ? 'block' : 'none';
    }

    init() {
        // Listen to local mouse movements only within the viewport
        if (this.viewport) {
            this.viewport.addEventListener('mousemove', (e) => {
                this.onLocalMouseMove(e);
            });

            this.viewport.addEventListener('mouseleave', () => {
                this.onLocalMouseLeave();
            });
        }
    }

    getViewportBounds() {
        if (!this.viewport) return null;
        return this.viewport.getBoundingClientRect();
    }

    onLocalMouseMove(event) {
        if (this.throttleTimer) return;

        this.throttleTimer = setTimeout(() => {
            this.throttleTimer = null;

            const bounds = this.getViewportBounds();
            if (!bounds || bounds.width === 0 || bounds.height === 0) return;

            // Calculate percentage coordinates relative to the simulation viewport
            // This ensures the same point in 3D space maps to the same percentage
            // regardless of sidebar visibility or screen size
            const relativeX = (event.clientX - bounds.left) / bounds.width;
            const relativeY = (event.clientY - bounds.top) / bounds.height;

            // Only send if cursor is within viewport bounds (0-1 range)
            if (relativeX >= 0 && relativeX <= 1 && relativeY >= 0 && relativeY <= 1) {
                if (this.ws.readyState === WebSocket.OPEN) {
                    this.ws.send(JSON.stringify({
                        type: 'cursor_move',
                        x: relativeX,
                        y: relativeY,
                        inViewport: true,
                        timestamp: Date.now()
                    }));
                }
            }
        }, this.THROTTLE_MS);
    }

    onLocalMouseLeave() {
        // Notify others that cursor left the viewport
        if (this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({
                type: 'cursor_leave',
                timestamp: Date.now()
            }));
        }
    }

    onRemoteCursorMove(userId, userName, x, y) {
        // Remove existing cursor if it belongs to me (shouldn't happen but safe guard)
        if (userId === this.userId) return;

        let cursor = this.otherCursors.get(userId);

        if (!cursor) {
            // Create new cursor element
            cursor = this.createCursorElement(userId, userName);
            this.otherCursors.set(userId, cursor);
            this.cursorContainer.appendChild(cursor);
        }

        // Convert percentage coordinates back to absolute position
        // relative to the local user's viewport
        const bounds = this.getViewportBounds();
        if (!bounds) return;

        // Calculate absolute position within the viewport
        const absX = bounds.left + (x * bounds.width);
        const absY = bounds.top + (y * bounds.height);

        cursor.style.transform = `translate(${absX}px, ${absY}px)`;

        // Update active status (used for cleanup or visuals)
        cursor.dataset.lastActive = Date.now();

        // Reset fade and hide timers
        cursor.classList.remove('faded');
        cursor.style.display = 'block';
        clearTimeout(cursor.fadeTimer);
        clearTimeout(cursor.hideTimer);

        // Fade after 3 seconds of inactivity
        cursor.fadeTimer = setTimeout(() => {
            cursor.classList.add('faded');

            // Hide completely after 2 more seconds (5 total)
            cursor.hideTimer = setTimeout(() => {
                cursor.style.display = 'none';
            }, 2000);
        }, 3000);
    }

    createCursorElement(userId, userName) {
        const cursor = document.createElement('div');
        cursor.className = 'remote-cursor';
        const color = this.getUserColor(userId);

        cursor.innerHTML = `
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
                <path d="M5.65376 12.3673H5.46026L5.31717 12.4976L0.500002 16.8829L0.500002 1.19177L11.7841 12.3673H5.65376Z" 
                 fill="${color}" stroke="${color}"/>
            </svg>
            <span class="cursor-label" style="background-color: ${color}">${this.escapeHtml(userName)}</span>
        `;
        return cursor;
    }

    getUserColor(userId) {
        // Generate consistent color based on userId
        const colors = [
            '#FF5722', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5',
            '#2196F3', '#03A9F4', '#00BCD4', '#009688', '#4CAF50',
            '#8BC34A', '#CDDC39', '#FFC107', '#FF9800', '#FF5722'
        ];
        let hash = 0;
        for (let i = 0; i < userId.length; i++) {
            hash = userId.charCodeAt(i) + ((hash << 5) - hash);
        }
        return colors[Math.abs(hash) % colors.length];
    }

    removeCursor(userId) {
        const cursor = this.otherCursors.get(userId);
        if (cursor) {
            cursor.remove();
            this.otherCursors.delete(userId);
        }
    }

    onRemoteCursorLeave(userId) {
        this.removeCursor(userId);
    }

    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    // Inject CSS for remote cursors
    static injectStyles() {
        if (document.getElementById('collaborative-cursor-styles')) return;

        const style = document.createElement('style');
        style.id = 'collaborative-cursor-styles';
        style.textContent = `
            #collaborative-cursors {
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                pointer-events: none;
                z-index: 10000;
            }

            .remote-cursor {
                position: absolute;
                top: 0;
                left: 0;
                pointer-events: none;
                transition: transform 0.05s linear;
                z-index: 10001;
            }

            .remote-cursor svg {
                filter: drop-shadow(1px 1px 2px rgba(0,0,0,0.5));
            }

            .remote-cursor .cursor-label {
                position: absolute;
                left: 20px;
                top: 16px;
                padding: 2px 8px;
                border-radius: 4px;
                font-size: 11px;
                font-weight: 500;
                color: white;
                white-space: nowrap;
                box-shadow: 0 2px 4px rgba(0,0,0,0.3);
            }

            .remote-cursor.faded {
                opacity: 0.3;
                transition: opacity 0.5s ease;
            }

            .toggle-pointers-button {
                position: fixed;
                bottom: 156px;
                right: 24px;
                width: 56px;
                height: 56px;
                border-radius: 50%;
                background: linear-gradient(135deg, #607D8B 0%, #455A64 100%);
                border: none;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                color: white;
                box-shadow: 0 4px 20px rgba(96, 125, 139, 0.4);
                transition: all 0.3s ease;
                z-index: 9999;
            }

            .toggle-pointers-button:hover {
                transform: scale(1.1);
                box-shadow: 0 6px 30px rgba(96, 125, 139, 0.6);
            }

            .toggle-pointers-button.active {
                background: linear-gradient(135deg, #2196F3 0%, #1976D2 100%);
                box-shadow: 0 4px 20px rgba(33, 150, 243, 0.4);
            }

            .toggle-pointers-button.active:hover {
                box-shadow: 0 6px 30px rgba(33, 150, 243, 0.6);
            }
        `;
        document.head.appendChild(style);
    }
}

// Inject styles on load
CollaborativeCursorManager.injectStyles();
